import React from 'react';
import DatePicker from 'Components/DatePicker';
import RadioButton, {
  ItemType as RadiobuttonItem
} from '@service-victoria/component-library/components/RadioButton';
import Text from '@service-victoria/component-library/components/Text';
import * as Interfaces from 'interfaces';
import * as Styled from '../../styled';
import {
  isVictorianReturningFromRedZone,
  isCrossBorderResident,
  isFullVaxERZVictorian
} from '../travel-reasons';
import { HomeAddress } from './Address/HomeAddress';
import DestinationAddress from './Address/DestinationAddress';
import LeavingFromAddress from './Address/LeavingFromAddress';
import {
  showRedZoneWarning,
  showOrangeZoneWarning,
  showGreenZoneWarning,
  showReturningHomeInvalidWarning,
  showTransitInvalidWarning
} from '../selectors';
import { showExtremelyRiskZone } from '../selectors';
import dayjs from 'dayjs';
import type { DeepPartial } from 'types/globals';
import { ExternalLinks, LastDayOfPermits } from 'utils/constants';
import { Values } from '../initialValues';

type TravelPermitFormProps = {
  setDeltaValues: (values: DeepPartial<Values>) => void;
  values: Values;
};

const fromOptions: RadiobuttonItem[] = [
  {
    label: 'Yes',
    value: 'home',
    inputProps: {
      'data-testid': 'HomeAddressYes-RadioButton'
    }
  },
  {
    label: 'No',
    value: 'other',
    inputProps: {
      'data-testid': 'HomeAddressNo-RadioButton'
    }
  }
];

const now = dayjs(new Date());

const today = now.format('YYYY-MM-DD');

export const TravelPermitForm: React.FC<TravelPermitFormProps> = ({ values, setDeltaValues }) => {
  const {
    assess: {
      Depart: {
        from,
        homeAddressIsDestination,
        quarantine: { fromQuarantine }
      },
      reasons: reasonCode
    }
  } = values as Interfaces.Common.FormValues;

  // Hardcoded for now as only individuals from overseas require border permit
  const isInternationalTravelPermit = true;

  return (
    <>
      <Styled.Spacer24 />
      <DatePicker
        name='assess.Depart.date'
        label='Planned entry date'
        min={today}
        max={LastDayOfPermits}
        data-testid='DepartDate-DatePicker'
        errorLabelProps={{
          'data-testid': 'DepartDate-DatePickerError'
        }}
      />
      {!isCrossBorderResident(reasonCode) && (
        <LeavingFromAddress
          setDeltaValues={setDeltaValues}
          isInternationalArrivalPermit={isInternationalTravelPermit}
        />
      )}
      {showExtremelyRiskZone(values) && (
        <>
          <Styled.NotificationInfo
            data-testid='ExtremeRiskZone-Notification'
            value={[
              'This address is currently in an extreme risk zone. You can’t travel from here right now, unless you’re a Specified Worker, or if you’re briefly travelling through Victoria to another state or territory.\n\n',
              'Travellers from an extreme risk zone must have a valid permit or exemption. If you don’t have one of these, check back later when the zones have changed.'
            ]}
          />
          <Styled.Spacer32 />
        </>
      )}
      {showRedZoneWarning(values) && (
        <>
          <Styled.NotificationInfo
            data-testid='RedZone-Notification'
            aria-invalid='true'
            aria-labelledby='assess.Depart.fromAddress.summaryData.label'
            value={[
              'This address is currently in a red zone. You need to apply for a red zone permit.'
            ]}
          />
          <Styled.Spacer32 />
        </>
      )}
      {showOrangeZoneWarning(values) && (
        <>
          <Styled.NotificationInfo
            data-testid='OrangeZone-Notification'
            aria-invalid='true'
            aria-labelledby='assess.Depart.fromAddress.summaryData.label'
            value={[
              'This address is currently in an orange zone. You need to apply for an Orange zone permit.'
            ]}
          />
          <Styled.Spacer32 />
        </>
      )}
      {showGreenZoneWarning(values) && (
        <>
          <Styled.NotificationInfo
            data-testid='GreenZone-Notification'
            aria-invalid='true'
            aria-labelledby='assess.Depart.fromAddress.summaryData.label'
            value={[
              'This address is currently in a green zone. You need to apply for a green zone permit.'
            ]}
          />
          <Styled.Spacer32 />
        </>
      )}
      {showReturningHomeInvalidWarning(values) && (
        <>
          <Styled.NotificationInfo
            data-testid='ReturningHomeInvalid-Notification'
            aria-invalid='true'
            aria-labelledby='assess.Depart.fromAddress.summaryData.label'
            value={[
              <>
                This address is not in the{' '}
                <Text.Link
                  href={ExternalLinks.CrossBorderLgaList}
                  value='cross-border community Local Government Area'
                />
                .
              </>,
              "You can't travel from here right now, unless you're a Specified Worker, or if you're briefly travelling through Victoria to another state or territory."
            ]}
          />
          <Styled.Spacer32 />
        </>
      )}
      {showTransitInvalidWarning(
        reasonCode,
        values.assess.Depart.fromAddress.fullDetails.state
      ) && (
        <>
          <Styled.NotificationInfo
            data-testid='TransitLeavingFromInvalid-Notification'
            aria-invalid='true'
            aria-labelledby='assess.Depart.fromAddress.summaryData.label'
            value='This address is in Victoria. Your leaving from address must not be in Victoria.'
          />
          <Styled.Spacer32 />
        </>
      )}
      <Styled.Spacer8 />
      {!isVictorianReturningFromRedZone(reasonCode) &&
        !isCrossBorderResident(reasonCode) &&
        !fromQuarantine &&
        !isFullVaxERZVictorian(reasonCode) && (
          <RadioButton
            name='assess.Depart.from'
            groupLabel='This is my home address'
            items={fromOptions}
            errorLabelProps={{
              'data-testid': 'HomeAddress-RadioButtonError'
            }}
          />
        )}
      {(from === 'other' ||
        isVictorianReturningFromRedZone(reasonCode) ||
        isCrossBorderResident(reasonCode) ||
        isFullVaxERZVictorian(reasonCode)) && (
        <HomeAddress
          setDeltaValues={setDeltaValues}
          isInternationalArrivalPermit={isInternationalTravelPermit}
        />
      )}
      <Styled.Spacer24 />
      {!isCrossBorderResident(reasonCode) && !homeAddressIsDestination && (
        <DestinationAddress setDeltaValues={setDeltaValues} />
      )}
    </>
  );
};
